<script>
import axios from '@axios'
import { BFormGroup, BFormRadioGroup } from 'bootstrap-vue'
import Overlay from "@core/components/common/Overlay.vue";

export default {
    components: { BFormGroup, BFormRadioGroup, Overlay },
    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
        value: {
            default: null,
        },
        config: {
            type: Object,
            default() {
                return {
                    properties: {},
                };
            }
        },
        list: {
            type: Object,
            default: null,
        },
        substance: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            questions: [],
            answersById: {},
            isLoading: false,
            loadError: false,
        };
    },
    computed: {
        propsConfig() {
            return this.config && this.config.properties ? this.config.properties : {};
        },
        label() {
            if (this.propsConfig.label) {
                return this.$t(this.propsConfig.label);
            }
            return this.config.type || '';
        },
        placeholder() {
            return this.propsConfig.placeholder ? this.$t(this.propsConfig.placeholder) : '';
        },
        required() {
            return this.propsConfig.required === true;
        },
        questionIdKey() {
            return this.propsConfig.questionIdKey || 'id';
        },
        questionTitleKey() {
            return this.propsConfig.questionTitleKey || 'title';
        },
        answerValueKey() {
            return this.propsConfig.answerValueKey || 'value';
        },
        responseQuestionIdKey() {
            return this.propsConfig.responseQuestionIdKey || this.questionIdKey;
        },
        optionsUrlTemplate() {
            return this.propsConfig.optionsUrlTemplate || '';
        },
        optionsUrlParams() {
            return this.propsConfig.optionsUrlParams || {};
        },
        urlPlaceholdersResolved() {
            return Object.keys(this.optionsUrlParams).every((placeholderKey) => {
                const sourceKey = this.optionsUrlParams[placeholderKey];
                return this.resolveContextValue(sourceKey) !== null;
            });
        },
        optionsUrl() {
            if (!this.optionsUrlTemplate || !this.urlPlaceholdersResolved) {
                return null;
            }

            let url = this.optionsUrlTemplate;
            Object.keys(this.optionsUrlParams).forEach((placeholderKey) => {
                const sourceKey = this.optionsUrlParams[placeholderKey];
                const value = this.resolveContextValue(sourceKey);
                url = url.replace(`{${placeholderKey}}`, encodeURIComponent(value));
            });

            return url;
        },
        radioOptions() {
            return [
                { text: this.$t('Component(Choice):yes'), value: true },
                { text: this.$t('Component(Choice):no'), value: false },
            ];
        },
        hasAnyAnswer() {
            return this.questions.some((question) => this.answerForQuestion(question) !== null);
        },
        mandatoryClass() {
            return this.required && !this.hasAnyAnswer ? 'input-bg-warning' : '';
        }
    },
    created() {
        this.loadQuestions();
    },
    watch: {
        value: {
            deep: true,
            handler() {
                this.hydrateAnswersFromValue();
            }
        },
        optionsUrl() {
            this.loadQuestions();
        }
    },
    methods: {
        resolveContextValue(sourceKey) {
            if (sourceKey === 'listId') {
                return this.list && this.list.id ? this.list.id : null;
            }
            if (sourceKey === 'substanceId') {
                return this.substance && this.substance.id ? this.substance.id : null;
            }
            return null;
        },
        questionId(question) {
            return question[this.questionIdKey];
        },
        questionTitle(question) {
            return question[this.questionTitleKey];
        },
        answerForQuestion(question) {
            const questionId = this.questionId(question);
            return this.answersById.hasOwnProperty(questionId) ? this.answersById[questionId] : null;
        },
        readOnlyAnswerText(question) {
            const answer = this.answerForQuestion(question);
            if (answer === true) {
                return this.$t('Component(Choice):yes');
            }
            if (answer === false) {
                return this.$t('Component(Choice):no');
            }
            return '-';
        },
        setAnswer(question, value) {
            const questionId = this.questionId(question);
            this.$set(this.answersById, questionId, value);
            this.emitValue();
        },
        hydrateAnswersFromValue() {
            const mapped = {};
            if (Array.isArray(this.value)) {
                this.value.forEach((item) => {
                    if (!item || item[this.responseQuestionIdKey] === undefined) {
                        return;
                    }
                    mapped[item[this.responseQuestionIdKey]] = item[this.answerValueKey];
                });
            }
            this.answersById = mapped;
        },
        emitValue() {
            const mapped = this.questions
                .map((question) => {
                    const id = this.questionId(question);
                    const answer = this.answersById[id];
                    if (answer === null || answer === undefined) {
                        return null;
                    }
                    return {
                        [this.responseQuestionIdKey]: id,
                        [this.answerValueKey]: answer,
                    };
                })
                .filter((item) => item !== null);

            this.$emit('input', mapped);
        },
        async loadQuestions() {
            this.loadError = false;
            this.questions = [];

            if (!this.optionsUrl) {
                return;
            }

            this.isLoading = true;
            try {
                const response = await axios.get(this.optionsUrl);
                this.questions = Array.isArray(response.data) ? response.data : [];
                this.hydrateAnswersFromValue();
            } catch (e) {
                this.loadError = true;
            } finally {
                this.isLoading = false;
            }
        }
    }
}
</script>

<template>
    <div v-if="isLoading || questions.length > 0">
        <div v-if="readOnly">
            <h5>{{ label }}</h5>
            <Overlay :show="isLoading">
                <div class="mb-1">
                    <div
                        v-for="question in questions"
                        :key="questionId(question)"
                        class="pb-50"
                    >
                        <div>{{ questionTitle(question) }}</div>
                        <div class="text-muted">{{ readOnlyAnswerText(question) }}</div>
                    </div>
                </div>
            </Overlay>
        </div>
        <b-form-group
            v-else
            :label="label"
        >
            <Overlay :show="isLoading">
                <div
                    :class="mandatoryClass"
                    class="p-50 rounded"
                >
                    <div
                        v-for="question in questions"
                        :key="questionId(question)"
                        class="pb-50"
                    >
                        <div>{{ questionTitle(question) }}</div>
                        <b-form-radio-group
                            :name="`restriction-${config.type}-${questionId(question)}`"
                            :checked="answerForQuestion(question)"
                            :options="radioOptions"
                            size="sm"
                            buttons
                            button-variant="outline-primary"
                            class="mb-0 mt-25"
                            @change="(value) => setAnswer(question, value)"
                        />
                    </div>
                </div>
            </Overlay>
        </b-form-group>
    </div>
</template>
